// src/components/ScanUpload/ScanUpload.tsx - Updated to hide completely when hideButton is true
import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Cloud, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { ImageFile, Scene } from '../../types/core.types';
import { UploadResult, UseS3UploadOptions } from '../../types/upload.types';
import { useS3Upload } from '../../hooks/useS3upload';
import { UploadProgress } from './UploadProgress';
import { UploadStatus } from './UploadStatus';

interface Props extends UseS3UploadOptions {
  images: ImageFile[];
  scene: Scene; // Required scene data for metadata generation
  className?: string;
  hideButton?: boolean;
  hideCompletely?: boolean; // New prop to hide the entire component
  onUploadStateChange?: (isUploading: boolean) => void; // Callback for upload state changes
}

export interface ScanUploadRef {
  startUpload: (images?: ImageFile[], scene?: Scene) => Promise<void>;
  cancelUpload: () => void;
  resetUpload: () => void;
}

export const ScanUpload = forwardRef<ScanUploadRef, Props>(({
  images,
  scene, // Extract scene from props
  className = '',
  autoStart = false,
  validationResults,
  callbacks,
  hideButton = false,
  hideCompletely = false, // Default to false for backward compatibility
  onUploadStateChange
}, ref) => {
  const [result, setResult] = useState<UploadResult | undefined>();

  // Use the upload hook with callbacks
  const { progress, startUpload, cancelUpload, resetUpload } = useS3Upload({
    autoStart,
    validationResults,
    callbacks: {
      ...callbacks,
      onComplete: (uploadResult) => {
        setResult(uploadResult);
        callbacks?.onComplete?.(uploadResult);
      },
      onError: (error) => {
        setResult(undefined);
        callbacks?.onError?.(error);
      }
    }
  });

  // Notify parent component about upload state changes
  useEffect(() => {
    onUploadStateChange?.(progress.status === 'uploading' || progress.status === 'getting-credentials');
  }, [progress.status, onUploadStateChange]);

  // Handle retry - restart upload with same images and scene
  const handleRetry = () => {
    setResult(undefined);
    startUpload(images, scene);
  };

  // Handle reset - clear state and result
  const handleReset = () => {
    setResult(undefined);
    resetUpload();
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    startUpload: (imagesToUpload = images, sceneData = scene) => startUpload(imagesToUpload, sceneData),
    cancelUpload,
    resetUpload: handleReset
  }));

  // Don't render if no images and status is idle
  if (images.length === 0 && progress.status === 'idle') {
    return null;
  }

  // Don't render at all when hideCompletely is true and status is idle
  if (hideCompletely && progress.status === 'idle') {
    return null;
  }

  // Only show progress and status when there's actual upload activity
  const showOnlyProgress = hideButton && progress.status === 'idle';
  if (showOnlyProgress) {
    return null;
  }

  const getHeaderIcon = () => {
    switch (progress.status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-400" />;
      case 'getting-credentials':
        return <Loader2 className="w-6 h-6 text-[#0CB4E4] animate-spin" />;
      default:
        return <Cloud className="w-6 h-6 text-[#0CB4E4]" />;
    }
  };

  const getHeaderTitle = () => {
    switch (progress.status) {
      case 'getting-credentials':
        return 'Preparing Upload';
      case 'uploading':
        return 'Uploading images';
      case 'completed':
        return 'Upload Complete';
      case 'error':
        return 'Upload Failed';
      case 'cancelled':
        return 'Upload Cancelled';
      default:
        return 'Upload Ready';
    }
  };

  const getHeaderDescription = () => {
    if (progress.status === 'idle') {
      return `Ready to upload ${images.length} image${images.length === 1 ? '' : 's'} for scene "${scene.name}"`;
    }
    
    if (progress.status === 'getting-credentials') {
      return 'Getting S3 credentials and preparing folder structure...';
    }

    if (progress.status === 'uploading') {
      return `Uploading images with metadata`;
    }

    if (progress.status === 'completed') {
      return `Successfully uploaded ${progress.uploadedFiles} image${progress.uploadedFiles === 1 ? '' : 's'} with session metadata`;
    }

    if (progress.status === 'error') {
      return 'Something went wrong during the upload process';
    }

    if (progress.status === 'cancelled') {
      return 'Upload was cancelled before completion';
    }

    return '';
  };

  return (
    <div className={`relative z-10 bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 p-6 ${className}`}>
      {/* Header - only show when there's active upload progress */}
      {(progress.status !== 'idle' || !hideButton) && (
        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              progress.status === 'completed' ? 'bg-green-900/30' :
              progress.status === 'error' ? 'bg-red-900/30' :
              'bg-[#0CB4E4]/20'
            }`}>
              {getHeaderIcon()}
            </div>
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">
              {getHeaderTitle()}
            </h3>
            <p className="text-sm text-gray-300 mt-1">
              {getHeaderDescription()}
            </p>
          </div>
        </div>
      )}

      {/* Progress Component - only show during active upload */}
      {progress.status !== 'idle' && (
        <UploadProgress 
          progress={progress} 
          onCancel={cancelUpload}
        />
      )}

      {/* Status Component - only show when there are results */}
      {(progress.status === 'completed' || progress.status === 'error' || progress.status === 'cancelled') && (
        <UploadStatus 
          progress={progress}
          result={result}
          onRetry={handleRetry}
          onReset={handleReset}
        />
      )}

      {/* Manual Start Button - only when not hiding and in idle state */}
      {!autoStart && progress.status === 'idle' && images.length > 0 && !hideButton && (
        <div className="mt-4">
          <button
            onClick={() => startUpload(images, scene)}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#0C5484] text-white font-medium rounded-lg hover:bg-[#0CB4E4]/80 transition-colors shadow-md"
          >
            <Cloud className="w-5 h-5" />
            Start Upload
          </button>
        </div>
      )}
    </div>
  );
});

ScanUpload.displayName = 'ScanUpload';
